import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Asset, ReplaceKind } from '@avs/contract';
import { apiClient } from '../../api/client.js';
import { showToast } from '../../hooks/useToasts.js';
import { useMe } from '../../hooks/useMe.js';
import { Icon } from '../../icons/Icon.js';
import { Button } from '../Button.js';
import { KIND_LABEL, KIND_PROMPT_PLACEHOLDER, STRATEGIES } from '../../utils/kinds.js';
import { StepPairing } from './StepPairing.js';
import { StepParams } from './StepParams.js';
import { StepPreview } from './StepPreview.js';
import { WIZARD_STEPS, expandPairs, type WizardState, type WizardStep } from './types.js';

type Props = {
  kind: ReplaceKind;
  onClose: () => void;
  onDone: () => void;   // 执行完成后回调（父级刷批次表）
};

function defaultName(kind: ReplaceKind): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${KIND_LABEL[kind]}批次 · ${mm}/${dd} ${hh}:${mi}`;
}

export function ReplaceWizard({ kind, onClose, onDone }: Props) {
  const { refresh: refreshMe } = useMe();
  const [step, setStep] = useState<WizardStep>(0);
  const [state, setState] = useState<WizardState>(() => ({
    kind,
    name: defaultName(kind),
    pairs: [],
    params: {
      strategy: STRATEGIES[kind][0] ?? '完全替换',
      resolution: '1080p',
      duration: '10 秒',
      retries: 1,
      promptOverride: '',
      replaceTarget: '',
    },
  }));

  // 执行态
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ total: 0, done: 0, failed: 0 });
  const [errors, setErrors] = useState<string[]>([]);   // 失败原因（不再吞错）

  // 素材名缓存（预览/执行展示）：从已加载过的 asset 累积
  const [names, setNames] = useState<Record<string, string>>({});
  const assetName = (id: string) => names[id] ?? id;
  // 配对步会 list asset；这里订阅一下让名字进缓存（简单做法：每次进预览拉一次所有素材）
  useEffect(() => {
    if (step !== 2 && step !== 3) return;
    let cancelled = false;
    Promise.all([
      apiClient.listAssets({ kind: 'video', limit: 100 }),
      apiClient.listAssets({ kind: 'image', limit: 100 }),
      apiClient.listAssets({ kind: 'group', limit: 100 }),
    ]).then((rs) => {
      if (cancelled) return;
      const m: Record<string, string> = {};
      for (const r of rs) for (const a of r.items as Asset[]) m[a.id] = a.name;
      setNames((prev) => ({ ...prev, ...m }));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [step]);

  const tasks = useMemo(() => expandPairs(state.pairs), [state.pairs]);

  // 门禁
  const canNext = useMemo(() => {
    if (step === 0) return state.pairs.length > 0 && state.pairs.every((p) => p.refAssetIds.length > 0);
    return true;
  }, [step, state.pairs]);

  // Esc 关闭（执行中不允许）
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !running) onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [running, onClose]);

  async function execute() {
    setRunning(true);
    setStep(3);
    setProgress({ total: tasks.length, done: 0, failed: 0 });
    setErrors([]);
    const prompt = state.params.promptOverride.trim() || KIND_PROMPT_PLACEHOLDER[kind];
    let done = 0, failed = 0;
    const errs: string[] = [];
    for (let i = 0; i < tasks.length; i++) {
      const t = tasks[i]!;
      try {
        const b = await apiClient.createBatch({
          name: `${state.name} #${i + 1}`,
          kind,
          sourceAssetId: t.videoAssetId,
          referenceAssetId: t.refAssetId,
          prompt,
          strategy: state.params.strategy,
          params: {
            resolution: state.params.resolution, duration: state.params.duration,
            concurrency: 1, retries: state.params.retries,
            replaceTarget: kind === 'object' ? (state.params.replaceTarget.trim() || undefined) : undefined,
          },
          outputLocation: `r2://avs-assets/output/${kind}/`,
          intent: 'submit',
        });
        // createBatch 即使提交模型失败也会 200 返回 status=failed（失败不扣额度）—— 据此判定
        if (b && b.status === 'failed') {
          failed++;
          errs.push(`#${i + 1}：${b.errorMessage || '生成失败'}`);
        } else {
          done++;
        }
      } catch (e) {
        failed++;
        errs.push(`#${i + 1}：${e instanceof Error ? e.message : String(e)}`);
      }
      setProgress({ total: tasks.length, done, failed });
    }
    setErrors(errs);
    refreshMe();
    setRunning(false);
    if (failed === 0) showToast(`已提交 ${done} 个任务`, 'ok');
    else showToast(`提交完成：成功 ${done} / 失败 ${failed}`, failed === done ? 'err' : 'info');
  }

  const stepDone = step === 3 && !running && progress.total > 0;

  return createPortal(
    <div className="wiz-overlay">
      <div className="wiz">
        {/* 顶栏：标题 + stepper + 关闭 */}
        <div className="wiz-top">
          <button type="button" className="icon-btn" onClick={onClose} disabled={running} title="返回">
            <Icon name="arrowRight" size={18} style={{ transform: 'rotate(180deg)' }} />
          </button>
          <div className="wiz-title">
            <input
              className="wiz-title-input"
              value={state.name}
              onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
              disabled={running}
            />
            <span className="wiz-counter">{state.pairs.length} 行 / {tasks.length} 任务</span>
          </div>
          <div className="wiz-stepper">
            {WIZARD_STEPS.map((label, i) => (
              <div key={label} className={'wiz-step' + (i === step ? ' active' : '') + (i < step ? ' done' : '')}>
                <span className="wiz-step-n">{i < step ? <Icon name="check" size={12} /> : i + 1}</span>
                <span className="wiz-step-label">{label}</span>
                {i < WIZARD_STEPS.length - 1 && <span className="wiz-step-line" />}
              </div>
            ))}
          </div>
          <div style={{ width: 32 }} />
        </div>

        {/* 内容 */}
        <div className="wiz-body">
          {step === 0 && <StepPairing pairs={state.pairs} onChange={(pairs) => setState((s) => ({ ...s, pairs }))} />}
          {step === 1 && <StepParams kind={kind} params={state.params} onChange={(params) => setState((s) => ({ ...s, params }))} />}
          {step === 2 && <StepPreview state={state} assetName={assetName} />}
          {step === 3 && (
            <div className="wiz-execute">
              <div className="card" style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
                <div className="wiz-exec-icon"><Icon name={stepDone ? 'checkCircle' : 'refresh'} size={28} /></div>
                <div className="block-title">{running ? '正在提交…' : stepDone ? '提交完成' : '准备执行'}</div>
                <div className="wiz-progress-bar"><div className="wiz-progress-fill" style={{ width: `${progress.total ? (progress.done + progress.failed) / progress.total * 100 : 0}%` }} /></div>
                <div className="muted">{progress.done + progress.failed} / {progress.total} · 成功 {progress.done} · 失败 {progress.failed}</div>
                {errors.length > 0 && (
                  <div className="alert alert-danger wiz-errs" style={{ marginTop: 'var(--space-s)', textAlign: 'left' }}>
                    <Icon name="warning" size={18} />
                    <div className="alert-body">
                      <div style={{ fontWeight: 'var(--font-weight-medium)' }}>失败原因（额度未扣）：</div>
                      {errors.map((m, i) => <div key={i} className="small" style={{ marginTop: 2 }}>{m}</div>)}
                      <div className="small muted" style={{ marginTop: 6 }}>
                        常见：未选参考图片 / 素材不存在 / fal 余额不足。物体替换需要「视频 + 参考图」各一。
                      </div>
                    </div>
                  </div>
                )}
                {stepDone && (
                  <Button variant="primary" style={{ marginTop: 'var(--space-m)' }} onClick={() => { onDone(); onClose(); }}>
                    查看任务管理
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 底栏：上一步 / 下一步 / 提交执行 */}
        {step < 3 && (
          <div className="wiz-foot">
            <Button variant="ghost" onClick={step === 0 ? onClose : () => setStep((s) => (s - 1) as WizardStep)}>
              {step === 0 ? '取消' : '上一步'}
            </Button>
            <div className="grow" />
            {step < 2 ? (
              <Button variant="primary" disabled={!canNext} rightIcon={<Icon name="arrowRight" size={16} />}
                onClick={() => setStep((s) => (s + 1) as WizardStep)}
                title={!canNext ? '每行至少选 1 个参考素材' : undefined}>
                下一步：{WIZARD_STEPS[step + 1]}
              </Button>
            ) : (
              <Button variant="primary" leftIcon={<Icon name="check" size={16} />} onClick={() => void execute()}>
                提交执行（{tasks.length} 个任务 / {tasks.length} 额度）
              </Button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
